<template>
  <q-page
    :class="{
      'q-pa-sm': $q.screen.lt.sm,
      'q-pa-md': !$q.screen.lt.sm
    }"
  >
    <div class="q-mb-md row">
      <div
        class="q-my-none col text-overline text-dark text-uppercase"
      >
        {{ $t('run.overview.textTitle') }}
      </div>
      <q-btn
        v-if="lastSession"
        icon="history"
        flat
        color="dark"
        dense
        @click="onShowLast"
      >
        <q-tooltip>
          {{ $t('run.collectData.buttonHistory') }}
        </q-tooltip>
      </q-btn>
    </div>
    <banner
      v-if="showAppointmentNoteBanner"
      :heading="$t('run.overview.appointmentNote')"
      :message="linkedAppointment.notes"
      variant="multi-line"
      subtype="note"
      class="appointment-note-banner q-mb-md"
    />
    <banner
      v-if="hasNoActivePrograms"
      type="warning"
      class="q-mb-md"
      :message="$t('run.overview.noProgramsText')"
    />
    <banner
      v-if="hasLinkedAppointment"
      data-e2e="banner-linked-appointment"
      show-icon
      :type="isOwnAppointment ? 'info' : 'error'"
      :subtype="isOwnAppointment && 'appointment'"
      :message="appointmentLabel"
      class="q-mb-md"
    >
      <template #action>
        <q-btn
          icon="more_vert"
          flat
          round
        >
          <q-menu>
            <q-item
              v-close-popup
              clickable
              class="text-no-wrap"
              @click="onEditAppointment"
            >
              <q-item-section avatar>
                <q-icon name="edit_calendar" />
              </q-item-section>
              <q-item-section>
                {{ $t('run.overview.appointmentEdit') }}
              </q-item-section>
            </q-item>
            <q-item
              v-if="isOwnAppointment"
              v-close-popup
              clickable
              class="text-no-wrap"
              @click="onUnlinkAppointment"
            >
              <q-item-section avatar>
                <q-icon name="event_busy" />
              </q-item-section>
              <q-item-section>
                {{ $t('run.overview.appointmentUnlink') }}
              </q-item-section>
            </q-item>
          </q-menu>
        </q-btn>
      </template>
    </banner>

    <session-preparation-content
      :pre-load-session="session"
      :pre-load-programs="shownSessionPrograms"
    />

    <q-page-sticky
      v-if="!hasNoActivePrograms"
      position="bottom-right"
      :offset="[18, 18]"
    >
      <q-btn
        v-ripple
        fab
        color="positive"
        icon="play_arrow"
        :loading="isLoading || isPreparing"
        @click="onStartSession"
      />
    </q-page-sticky>
    <div class="q-py-md q-gutter-sm">
      <q-btn
        v-if="hasNoPrograms"
        v-ripple
        :label="$t('run.overview.backButton')"
        color="dark-variant"
        :to="{ name: 'Home' }"
      />
      <q-btn
        v-else
        v-ripple
        color="dark-variant"
        :label="$t('common.buttonCancel')"
        data-e2e="button-cancel"
        @click="onCancelSession"
      />
      <q-btn
        v-if="showStartVideoSession && !hasNoPrograms"
        v-ripple
        color="primary"
        :label="$t('run.overview.buttonStartVideoSession')"
        icon="voice_chat"
        :loading="isLoading || isPreparing"
        :disable="offlineMode"
        data-e2e="button-start-video-session"
        @click="onStartVideoSession"
      />
      <q-btn
        v-if="!hasNoActivePrograms"
        v-ripple
        color="positive"
        icon="play_arrow"
        :label="$t('run.overview.buttonStartSession')"
        :loading="isLoading || isPreparing"
        data-e2e="button-start-session"
        @click="onStartSession"
      />
    </div>
  </q-page>
</template>

<script>
import ChangeSessionActivityAppointment from 'components/SessionActivities/Modals/ChangeSessionActivityAppointment';
import SessionCardDialog from 'components/Sessions/Modals/SessionCardDialog';
import SessionPreparationContent from 'components/Sessions/SessionPreparationContent';
import Banner from 'components/UI/Banner';
import InviteUser from 'components/VideoChat/Modals/InviteUser';
import { Timestamp } from 'firebase/firestore';
import { chain, find, get, head, isEmpty, map, size } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useSessionActivities } from 'src/composables/sessionActivities';
import { useTime } from 'src/composables/time';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import { SESSION_STATUSES } from 'src/constants/sessions';
import AppointmentsDB from 'src/fb/appointments-db';
import GenericDb from 'src/fb/generic-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import Auth from 'src/mixins/Auth';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import Personalize from 'src/mixins/Personalize';
import SessionsMixin from 'src/mixins/SessionsMixin';
import { auth } from 'src/store/auth';
import { geolocation } from 'src/store/geolocation';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';
import { promptLevels } from 'src/store/promptLevels';
import { sessions } from 'src/store/sessions';
import { userInterface } from 'src/store/userInterface';
import { clearClientBackupData, clearClientBackupNotes } from 'src/utils/backupUtils';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'Overview',

  components: {
    SessionPreparationContent, Banner,
  },

  mixins: [
    Integrations,
    Interactions,
    Personalize,
    SessionsMixin,
    Auth,
  ],

  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      if (!vm.hasStartSessionsPrivilege) {
        vm.notifyNegative(vm.$t('sessions.sessionCard.disabledStartSession'));
        vm.$router.push('/');
      }

      /**
       * To make the UI as fast as possible we load and show programs from the scheduled / ad-hoc
       * session immediately on load. At the same time we ask a back-end function to prepare the
       * new session for us. We then load the new active session in the background.
       */
      vm.finishActiveSession();
      const { sessionId } = vm.$route.params;
      const appointmentId = get(vm.$route, ['query', 'appointment'], null);
      vm.closeLeftMenuDrawer();
      vm.setIsLoading(true);
      await vm.loadSession(sessionId);
      vm.checkSessionRedirect();
      await vm.loadSessionPrograms(sessionId);
      vm.setIsLoading(false);
      vm.prepareNewSession(sessionId);
      vm.loadPreviousSession(sessionId);
      vm.loadLinkedAppointment(appointmentId);
    });
  },

  setup() {
    const { checkUserCanStartSessionActivity } = useSessionActivities();
    const { getLocaleDateString } = useTime();

    return { checkUserCanStartSessionActivity, getLocaleDateString };
  },

  data() {
    return {
      isLoading: false,
      isPreparing: false,

      session: null,
      programsInSession: [],
      linkedAppointment: null,

      sessionId: null,
      appointmentId: null,
      startVideoSession: false,
      selectedUser: null,

      lastSession: null,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'currentClient', 'clientAppointmentsUpcoming',
      'hasTrainerCredentials', 'hasTherapistCredentials', 'hasSupervisorCredentials',
    ]),
    ...mapState(sessions, ['videoSessionMembersNames', 'scheduledSessions',
      'activeSession', 'activeSessionPrograms',
    ]),
    ...mapState(programs, ['programs', 'allPrograms']),
    ...mapState(network, ['offlineMode']),

    shownSessionPrograms() {
      return chain(this.programsInSession)
        .map((programId) => find(this.programs, { id: programId }))
        .compact()
        .filter((program) => ACTIVE_PROGRAM_STATUSES.includes(program.status))
        .value();
    }, // shownSessionPrograms()

    showStartVideoSession() {
      return this.hasTherapistCredentials;
    }, // showStartVideoSession()

    isOwnAppointment() {
      return get(this.linkedAppointment, 'user') === this.userDetails.userId;
    }, // isOwnAppointment

    hasNoActivePrograms() {
      return !this.isPreparing
        && get(this.activeSession, 'id')
        && isEmpty(this.shownSessionPrograms);
    }, // hasNoActivePrograms

    hasNoPrograms() {
      return isEmpty(this.activeProgramsIds);
    }, // hasNoPrograms

    hasRunningSessionWithCurrentClient() {
      return !!(this.scheduledSessions.find((session) => (
        !session.isDeleted
        && session.status === SESSION_STATUSES.RUNNING
        && session.runBy === this.userDetails.userId
        && session.client === this.currentClient.id
        && session.originalSessionId === this.activeSession.originalSessionId
      )));
    }, // hasRunningSessionWithCurrentClient

    activeProgramsIds() {
      return map(this.shownSessionPrograms, 'id');
    }, // activeProgramsIds

    hasLinkedAppointment() {
      return this.linkedAppointment !== null;
    }, // hasLinkedAppointment

    appointmentLabel() {
      const scheduledStartTimestamp = get(this.linkedAppointment, 'scheduledStartTimestamp');
      const dateTime = scheduledStartTimestamp instanceof Timestamp
        ? this.getLocaleDateString(scheduledStartTimestamp, { options: { hour: 'numeric', minute: '2-digit' } }) : '?';

      if (!this.isOwnAppointment) {
        return this.$t('run.overview.notOwnAppointment', {
          dateTime,
        });
      }

      return this.$t('run.overview.textAppointment', {
        dateTime,
      });
    }, // appointmentLabel

    hasStartSessionsPrivilege() {
      return this.userHasPrivilege('sessions/startSessions');
    }, // hasStartSessionsPrivilege

    showAppointmentNoteBanner() {
      return this.hasLinkedAppointment && this.linkedAppointment.notes
        && (this.linkedAppointment.isNotesVisible ?? true) && this.hasTrainerCredentials;
    }, // showAppointmentNoteBanner()
  },

  watch: {
    async startVideoSession(newVal) {
      const canUserCreateSession = await this.canUserCreateVideoSession();

      if (newVal && !canUserCreateSession) {
        this.$nextTick(() => {
          this.startVideoSession = false;

          this.alertDialog(
            this.$t('run.collectData.titleUserDoesNotHaveVideoMinutes'),
            this.$t('run.collectData.textUserDoesNotHaveVideoMinutes'),
            { persistent: true },
          );
        });
      }
    },
  },

  methods: {
    ...mapActions(sessions, [
      'loadActiveSession',
      'cancelActiveSession',
      'finishActiveSession',
      'setVideoSessionMembersNames',
    ]),
    ...mapActions(userInterface, ['openLeftMenuDrawer', 'closeLeftMenuDrawer']),
    ...mapActions(auth, ['canUserCreateVideoSession']),
    ...mapActions(programs, ['loadDeletedSessionPrograms']),
    ...mapActions(promptLevels, ['loadPromptLevelTemplates']),
    ...mapActions(geolocation, ['refreshGeolocation']),

    async loadLinkedAppointment(appointmentId) {
      if (!appointmentId) {
        this.appointmentId = null;
        this.linkedAppointment = null;
        return;
      }

      const foundAppointment = find(this.clientAppointmentsUpcoming, { id: appointmentId });

      // Try to find the appointment in the list of upcoming appointments to skip the database call
      if (foundAppointment) {
        this.linkedAppointment = foundAppointment;
        this.appointmentId = appointmentId;
        return;
      }

      const appointmentsDb = new AppointmentsDB();

      const loadedAppointment = await appointmentsDb.read(appointmentId);

      // Apply only scheduled appointments
      if (loadedAppointment && loadedAppointment.status === 'scheduled') {
        this.linkedAppointment = loadedAppointment;
        this.appointmentId = appointmentId;
        return;
      }

      this.linkedAppointment = null;
      this.appointmentId = null;
    }, // loadLinkedAppointment()

    async loadPreviousSession(sessionId, currentTimestamp = null, increment = 0) {
      if (increment > 10) {
        // Guard to prevent infinite loading
        return false;
      }

      const sessionsDb = new SessionsDB();

      const constraints = [
        ['client', '==', this.currentClient.id],
        ['status', 'in', ['done', 'processing']],
        ['originalSessionId', '==', sessionId],
      ];

      if (currentTimestamp) {
        constraints.push([
          'runTimestamp', '<', currentTimestamp,
        ]);
      }

      const previousSessions = await sessionsDb.readAll(constraints, 'runTimestamp', 'desc', 1);

      if (size(previousSessions) > 0) {
        const lastSession = head(previousSessions);

        if (lastSession.isDeleted) {
          return this.loadPreviousSession(sessionId, lastSession.runTimestamp, increment + 1);
        }

        this.lastSession = lastSession;
      }
    }, // loadPreviousSession()

    async onShowLast() {
      await this.loadDeletedSessionPrograms(this.lastSession.id);

      this.openComponentDialog(SessionCardDialog, {
        allPrograms: this.allPrograms,
        client: this.currentClient,
        session: this.lastSession,
      });
    }, // onShowLast()

    onEditAppointment() {
      this.openComponentDialog(ChangeSessionActivityAppointment, {
        appointmentId: this.appointmentId,
        clientId: this.currentClient.id,
        userId: this.userDetails.userId,
        startDate: new Date(),
      }, async (newAppointmentId) => {
        if (!newAppointmentId) {
          return;
        }
        this.loadLinkedAppointment(newAppointmentId);
      });
    }, // onEditAppointment()

    onUnlinkAppointment() {
      const confirmMessage = this.connectedClinic.integration
        ? this.$t('run.overview.confirmUnlinkAppointment', {
          integrationLabel: this.getIntegrationName(this.connectedClinic.integration),
        })
        : this.$t('run.overview.confirmUnlinkAppointmentNoIntegration');

      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        confirmMessage,
        () => {
          this.loadLinkedAppointment(null);
        },
      );
    },

    /**
     * Start a new session by updating session info in Firestore
     */
    async onStartSession() {
      if (!await this.checkUserCanStartSessionActivity(this.session.client || null)) {
        return;
      }

      if (this.hasRunningSessionWithCurrentClient) {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('run.collectData.warningHasOwnRunningSessionWithClient'),
          () => this.startSession(),
        );
        return;
      }

      return this.startSession();
    }, // onStartSession()

    async startSession() {
      this.$q.loading.show();

      // Start session state and update info
      const initialProgram = chain(this.activeSessionPrograms)
        .filter((sessionProgram) => !sessionProgram.pinned
          && !sessionProgram.hidden
          && (ACTIVE_PROGRAM_STATUSES.includes(get(sessionProgram, ['program', 'status']))))
        .head()
        .get('id', null)
        .value();

      try {
        if (this.isOwnAppointment && this.appointmentId) {
          await this.loadLinkedAppointment(this.appointmentId);
        }

        const appointmentId = this.isOwnAppointment ? this.appointmentId : null;

        // should use backend function in case if offline mode is not used
        if (!this.offlineMode) {
          const startSessionOptions = {
            sessionId: this.activeSession.id, initialProgramId: initialProgram, appointmentId,
          };
          await this.startRunningSessionViaBackend(startSessionOptions);
        } else {
          const genericDb = new GenericDb();
          await genericDb.executeTransaction(async (transactionMethods) => {
            await this.startRunningSession(
              this.activeSession,
              this.userDetails.userId,
              this.userDetails.name,
              initialProgram,
              appointmentId,
              null,
              transactionMethods,
            );
          });
        }
      } catch (err) {
        console.error(err);
        this.notifyNegative(this.$t('run.collectData.sessionNotActiveText'));
        logEvent(err);

        this.$q.loading.hide();
        this.$router.push('/');
        return;
      }

      this.refreshGeolocation(true);

      this.$q.loading.hide();

      const dataCollectionPage = {
        name: 'session-data-collection',
        params: {
          sessionId: this.activeSession.id,
        },
        replace: true,
      };

      if (this.startVideoSession && this.selectedUser) {
        dataCollectionPage.query = {
          join_call_with: this.selectedUser.user,
        };

        this.setVideoSessionMembersNames({
          ...this.videoSessionMembersNames,
          [this.selectedUser.user]: this.selectedUser.userName,
        });
      }

      await clearClientBackupData(this.currentClient.id);
      await clearClientBackupNotes(this.currentClient.id);
      this.$router.push(dataCollectionPage);
    }, // startSession()

    onStartVideoSession() {
      this.openComponentDialog(InviteUser, {
        submitButtonLabel: this.$t('run.overview.buttonSubmitAndStartSession'),
        title: this.$t('sessions.modals.callInvite.title'),
        subtitle: this.$t('sessions.modals.callInvite.subtitle'),
        sessionId: this.activeSession.id,
        skipUsers: [this.userDetails.userId],
      }, async (selectedUser) => {
        this.startVideoSession = true;
        this.selectedUser = selectedUser;
        this.onStartSession();
      }, () => {
        this.startVideoSession = false;
        this.selectedUser = null;
      });
    }, // onStartVideoSession()

    onCancelSession() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('run.overview.dialogCancelSessionMessage'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          this.$q.loading.show();
          try {
            this.cancelActiveSession();
            this.openLeftMenuDrawer();
          } finally {
            this.$q.loading.hide();
          }
          this.$router.push('/');
        });
    }, // onCancelSession()

    switchToSession(sessionId) {
      if (this.$route.name !== 'Session Overview') {
        return;
      }

      this.finishActiveSession();
      return this.loadActiveSession({
        sessionId,
        forceUpdate: true,
      });
    }, // switchToSession()

    async loadSession(sessionId) {
      this.session = chain(this.scheduledSessions).find({ id: sessionId }).defaultTo(null).value();
      if (this.session === null) {
        const sessionsDb = new SessionsDB(sessionId);
        this.session = await sessionsDb.read(sessionId);
      }

      if (get(this.session, 'isDeleted') === true) {
        this.notifyNegative(this.$t('run.overview.notifySessionDeleted'));
        this.$router.push('/');
        throw new Error('Session is deleted');
      }

      this.checkSessionVisibility();
    },

    checkSessionVisibility() {
      const { limitedVisibility, visibility } = this.session;
      const isSessionVisible = !limitedVisibility || this.hasSupervisorCredentials
        || visibility.includes(this.userDetails.userPrivilege || this.userDetails.role);

      if (isSessionVisible) {
        return;
      }

      return this.$q.dialog({
        message: this.$t('run.overview.dialogSessionVisibilityLimited'),
        cancel: false,
        persistent: true,
      }).onOk(async () => {
        this.$router.push('/');
      });
    },

    checkSessionRedirect() {
      if (!this.session || ['remote_async', 'training'].includes(this.session?.sessionType)) {
        this.$router.push({ name: 'Home' });
      }
    },

    async loadSessionPrograms(sessionId) {
      const sessionProgramsDb = new SessionProgramsDB(sessionId);

      this.programsInSession = chain(await sessionProgramsDb.readAll())
        .sortBy('order')
        .reject({ hidden: true })
        .map('program')
        .uniq()
        .value();
    }, // loadSessionPrograms()

    async initNewSession(sessionId) {
      const newSessionId = await this.prepareSession(sessionId);
      if (!newSessionId) {
        this.notifyNegative(this.$t('run.overview.notifySessionNotPrepared'));
        this.isPreparing = false;
        this.$router.push('/');
        return;
      }

      return this.switchToSession(newSessionId);
    }, // initNewSession()

    async prepareNewSession(sessionId) {
      this.isPreparing = true;
      await Promise.all([
        this.initNewSession(sessionId),
        this.loadPromptLevelTemplates(),
      ]);

      this.isPreparing = false;
    }, // prepareNewSession()

    setIsLoading(value) {
      this.isLoading = value;
    },

  },
};
</script>

<style scoped lang="scss">
.appointment-note-banner {
  :deep(.banner__heading) {
    font-weight: 600;
    margin-bottom: 8px;
  }
}
</style>
